import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { canAccessSubmissionsPage } from '../features/forms/utils/submissionPermissions';
import { Navbar, Nav, NavDropdown, Button } from '@/components/ui';
import { useNavbar } from '@/components/ui/Navbar';
import {
  ADMIN_NAV_GROUPS,
  PROCUREMENT_NAV_ITEMS,
  WORK_REQUESTS_NAV_ITEMS,
  isAdminMenuVisible,
  isGroupVisible,
  isEquipmentVisible,
  isTasksVisible,
  visibleGroupItems,
  visibleItems,
  type AdminNavGroup,
  type AdminNavItem,
} from './adminNavConfig';
import { pickCurrentPath } from './navCurrentPage';
import { computeFlyoutPlacement } from './flyoutPlacement';
import { useAwaitingApprovalsCount } from './useAwaitingApprovalsCount';
import { EQUIPMENT_NAV_PATH } from './awaitingApprovalsCount';
import { EQUIPMENT_PERMISSIONS } from '../features/equipment/utils/equipmentPermissions';
import NavCountBadge from './NavCountBadge';
import './NavBar.css';
import { AccountMenu } from './AccountMenu';
import { NAVBAR_BG, type ReleaseStage } from './navbarColors';

interface AdminGroupFlyoutProps {
  group: AdminNavGroup;
  items: AdminNavItem[];
  isOpen: boolean;
  /** The current page's route, so the group and its page can be marked (W-M10). */
  currentPath: string | null;
  onOpen: () => void;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

/**
 * One expandable group inside the Admin menu.
 *
 * Click or tap only (L9) - there are no hover handlers, so moving the mouse from a group towards
 * its flyout, even diagonally across other groups, never switches or closes it (W-H6).
 *
 * Desktop bar: clicking a group opens its flyout beside the Admin menu, flush against it, on the
 * right when it fits and otherwise on the left (`computeFlyoutPlacement`). Clicking it again
 * leaves it open; choosing a page, opening another group, a click outside the Admin menu or
 * Escape closes it.
 *
 * Drawer (below the `nav` breakpoint): the group is an accordion - one tap expands its pages
 * inline, indented under it, and a second tap folds it (W-iP2). Nothing floats off screen.
 *
 * The header's click is stopped from bubbling because the parent `NavDropdown` menu closes on any
 * click inside it - right for a page link, wrong for a header whose whole job is to open a submenu.
 */
const AdminGroupFlyout: React.FC<AdminGroupFlyoutProps> = ({
  group,
  items,
  isOpen,
  currentPath,
  onOpen,
  onClose,
  onNavigate,
}) => {
  const { collapsedLayout } = useNavbar();
  const anchorRef = useRef<HTMLElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{ top: number; left: number; side: 'left' | 'right' } | null>(null);
  const containsCurrent = items.some((item) => item.to === currentPath);
  const panelId = `admin-group-${group.id}`;

  // In the drawer, the group holding the current page starts expanded when Admin is opened, so
  // the marked page is visible without hunting for it. (This component mounts with the menu.)
  useLayoutEffect(() => {
    if (collapsedLayout && containsCurrent) onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Measure before paint so the flyout never flashes at a stale position.
  useLayoutEffect(() => {
    if (!isOpen || collapsedLayout) {
      setPlacement(null);
      return;
    }
    const place = () => {
      const anchor = anchorRef.current;
      const flyout = flyoutRef.current;
      if (!anchor || !flyout) return;
      const rect = anchor.getBoundingClientRect();
      const paddingTop = parseFloat(window.getComputedStyle(flyout).paddingTop) || 0;
      setPlacement(
        computeFlyoutPlacement({
          anchor: rect,
          width: flyout.offsetWidth,
          height: flyout.offsetHeight,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          offsetTop: paddingTop,
        })
      );
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [isOpen, collapsedLayout]);

  const renderItem = (item: AdminNavItem) => (
    <NavDropdown.Item
      key={item.to}
      as={Link}
      to={item.to}
      current={item.to === currentPath}
      className="tw-whitespace-nowrap"
      onClick={() => {
        onNavigate(item.to);
        onClose();
      }}
    >
      {item.label}
    </NavDropdown.Item>
  );

  if (collapsedLayout) {
    return (
      <div className="wrap-nav-group-wrap">
        <NavDropdown.Item
          ref={anchorRef}
          as="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          current={containsCurrent}
          aria-current={containsCurrent ? 'true' : undefined}
          className="wrap-nav-group tw-flex tw-min-h-[44px] tw-items-center tw-justify-between"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (isOpen) onClose();
            else onOpen();
          }}
        >
          {group.label}
          <i
            className={`bi bi-chevron-down tw-ml-2 tw-text-[0.7rem] tw-transition-transform tw-duration-150 ${isOpen ? 'tw-rotate-180' : ''}`}
            aria-hidden="true"
          />
        </NavDropdown.Item>
        {isOpen && (
          <div id={panelId} className="wrap-nav-group-items" role="group" aria-label={group.label}>
            {items.map(renderItem)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wrap-nav-group-wrap">
      <NavDropdown.Item
        ref={anchorRef}
        as="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={panelId}
        current={containsCurrent}
        aria-current={containsCurrent ? 'true' : undefined}
        className={`wrap-nav-group tw-flex tw-items-center tw-justify-between tw-whitespace-nowrap ${isOpen && !containsCurrent ? 'tw-bg-surface-hover' : ''}`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        {group.label}
        <i className="bi bi-chevron-right tw-ml-3" aria-hidden="true"></i>
      </NavDropdown.Item>
      {isOpen && (
        <div
          ref={flyoutRef}
          id={panelId}
          role="group"
          aria-label={group.label}
          data-side={placement?.side}
          className="submenu-dropdown tw-fixed tw-z-[1051] tw-w-max tw-min-w-[13rem] tw-max-w-[calc(100vw-1rem)] tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-py-1.5 tw-text-content tw-shadow-lg"
          style={{
            top: `${placement?.top ?? 0}px`,
            left: `${placement?.left ?? 0}px`,
            visibility: placement ? undefined : 'hidden',
          }}
        >
          {items.map(renderItem)}
        </div>
      )}
    </div>
  );
};

const NavBar: React.FC = () => {
  const location = useLocation();
  const { currentUser: user, logout, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const [openAdminGroupId, setOpenAdminGroupId] = useState<string | null>(null);

  const handleNavClick = (path: string) => {
    if (location.pathname === path) {
      window.location.reload();
    }
  };

  const procurementItems = visibleItems(PROCUREMENT_NAV_ITEMS, hasPermission);
  const workRequestItems = visibleItems(WORK_REQUESTS_NAV_ITEMS, hasPermission);
  // V2-035: awaiting counts only for queues this person can act on (the item's own gate).
  const countedPaths = procurementItems.filter((item) => item.countsAwaiting).map((item) => item.to);
  const roleName =
    typeof user?.role === 'string' ? user.role : user?.role?.name;
  // Admin auto-holds message_board:* via usePermissions; keep an explicit
  // role check too so a stale session permissions array cannot hide the tab.
  const messageBoardVisible =
    hasPermission('message_board:view') ||
    hasPermission('message_board:post') ||
    (roleName || '').trim().toLowerCase() === 'admin';
  const equipmentVisible = isEquipmentVisible(hasPermission);
  const { byPath: awaitingByPath, total: awaitingTotal } = useAwaitingApprovalsCount(
    !loading && countedPaths.length > 0,
    countedPaths,
    location.pathname
  );
  // EQ-30 / NAV-09: the Equipment badge (open Critical defects) is for Manage Repairs only;
  // everyone else sees Equipment with no count, and never makes the request.
  const showCriticalDefectsBadge = equipmentVisible && hasPermission(EQUIPMENT_PERMISSIONS.repairsManage);
  const { byPath: equipmentCounts } = useAwaitingApprovalsCount(
    !loading && showCriticalDefectsBadge,
    showCriticalDefectsBadge ? [EQUIPMENT_NAV_PATH] : [],
    location.pathname
  );
  const criticalDefectCount = showCriticalDefectsBadge ? equipmentCounts[EQUIPMENT_NAV_PATH] ?? 0 : 0;

  // Consistent environment detection with server
  // Priority: REACT_APP_ENVIRONMENT > NODE_ENV > default to 'development'
  const env = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const normalizedEnv = env.toLowerCase();

  // Validate environment (same as server)
  const validEnvironments = ['development', 'staging', 'training', 'testing', 'production'];
  const releaseStage = (validEnvironments.includes(normalizedEnv) ? normalizedEnv : 'development') as ReleaseStage;

  // Format environment string to capitalize first letter
  const formatEnv = (env: string) => {
    return env.charAt(0).toUpperCase() + env.slice(1).toLowerCase();
  };

  // Color the whole control bar per non-production stage so it's obvious at a glance, not
  // just readable on close inspection - training is the one most likely to be
  // mistaken for production by someone clicking through quickly. Applied as an inline style
  // (rather than a `tw-bg-*` class) because it has to beat the Navbar component's own
  // `tw-bg-ink` utility: two same-specificity background utilities are decided by which one
  // is later in the compiled stylesheet, not by which comes later in the class list, so a
  // second class here would be an unreliable way to override it.
  // `data-env` lets tailwind.css switch the current-page marker to white on these bars (V2-042).
  const navbarStyle: React.CSSProperties | undefined =
    releaseStage !== 'production' ? { backgroundColor: NAVBAR_BG[releaseStage] } : undefined;

  const envBadge = releaseStage !== 'production' && (
    <span className="tw-ml-2 tw-rounded tw-border tw-border-white/40 tw-bg-black/15 tw-px-2 tw-py-0.5 tw-text-xs tw-font-bold tw-tracking-wide tw-text-white">
      {formatEnv(releaseStage)}
    </span>
  );

  // Don't render navigation items while loading
  if (loading) {
    return (
      <Navbar bg="dark" variant="dark" expand="lg" className="tw-w-full tw-px-3 tw-relative tw-z-[1030] tw-shrink-0" style={navbarStyle} data-env={releaseStage}>
        <Navbar.Brand className="tw-flex tw-items-center">
          <Link to="/home" className="tw-flex tw-items-center tw-no-underline">
            <img
              src={encodeURI(`${process.env.PUBLIC_URL || ''}/WRAP logo no border.png`)}
              alt="WRAP Logo"
              style={{
                height: '30px',
                marginRight: '10px'
              }}
            />
          </Link>
          {envBadge}
        </Navbar.Brand>
        <Nav className="navbar-nav tw-flex-row tw-items-center tw-ml-2">
          <Nav.Link
            as={NavLink}
            to="/home"
            onClick={() => handleNavClick('/home')}
          >
            Home
          </Nav.Link>
        </Nav>
      </Navbar>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const tasksVisible = isTasksVisible(hasPermission);
  const adminVisible = isAdminMenuVisible(hasPermission);
  const adminGroups = adminVisible
    ? ADMIN_NAV_GROUPS.filter((group) => isGroupVisible(group, hasPermission)).map((group) => ({
        group,
        items: visibleGroupItems(group, hasPermission),
      }))
    : [];

  // The one dropdown destination that is the current page, across every menu (W-M10), so a
  // parent can carry the red underline and its menu can mark the page.
  const currentPath = pickCurrentPath(
    [
      ...procurementItems.map((item) => item.to),
      ...workRequestItems.map((item) => item.to),
      ...adminGroups.flatMap(({ items }) => items.map((item) => item.to)),
    ],
    location
  );
  const isCurrent = (item: AdminNavItem) => item.to === currentPath;
  const renderMenuItem = (item: AdminNavItem) => (
    <NavDropdown.Item
      key={item.to}
      as={Link}
      to={item.to}
      current={isCurrent(item)}
      onClick={() => handleNavClick(item.to)}
    >
      {item.countsAwaiting ? (
        <span className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3">
          {item.label}
          {awaitingByPath[item.to] > 0 && (
            <span className="tw-sr-only">{`, ${awaitingByPath[item.to]} awaiting`}</span>
          )}
          <NavCountBadge count={awaitingByPath[item.to] ?? 0} />
        </span>
      ) : (
        item.label
      )}
    </NavDropdown.Item>
  );
  const currentAdminGroupId =
    adminGroups.find(({ items }) => items.some(isCurrent))?.group.id ?? null;

  return (
    <Navbar bg="dark" variant="dark" expand="lg" className="tw-w-full tw-px-3 tw-relative tw-z-[1030] tw-shrink-0" style={navbarStyle} data-env={releaseStage}>
      {/* V2-040 / NAV-01: the hamburger on the left beside the logo, shown only when the full menu
          does not fit (measured). */}
      <Navbar.Toggle aria-controls="navbarNav" />
      <Navbar.Brand className="tw-flex tw-items-center">
        <Link to="/home" className="tw-flex tw-items-center tw-no-underline">
          <img
            src={encodeURI(`${process.env.PUBLIC_URL || ''}/WRAP logo no border.png`)}
            alt="WRAP Logo"
            style={{
              height: '30px',
              marginRight: '10px'
            }}
          />
        </Link>
        {envBadge}
      </Navbar.Brand>
      <Navbar.Collapse id="navbarNav">
        <Nav className="tw-mr-auto tw-gap-0.5">
          <Nav.Link
            as={NavLink}
            to="/home"
            onClick={() => handleNavClick('/home')}
          >
            Home
          </Nav.Link>
          {tasksVisible && (
            <Nav.Link
              as={NavLink}
              to="/tasks"
              onClick={() => handleNavClick('/tasks')}
            >
              Tasks
            </Nav.Link>
          )}
          {hasPermission('schedule:view') && (
            <Nav.Link
              as={NavLink}
              to="/schedule"
              onClick={() => handleNavClick('/schedule')}
            >
              Schedule
            </Nav.Link>
          )}

          {hasPermission('project:view') && (
            <Nav.Link
              as={NavLink}
              to="/projects"
              onClick={() => handleNavClick('/projects')}
            >
              Projects
            </Nav.Link>
          )}

          {canAccessSubmissionsPage(hasPermission) && (
            <Nav.Link
              as={NavLink}
              to="/submissions"
              onClick={() => handleNavClick('/submissions')}
            >
              Submissions
            </Nav.Link>
          )}
          {procurementItems.length > 0 && (
            <NavDropdown
              title={
                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                  Procurement
                  {awaitingTotal > 0 && (
                    <span className="tw-sr-only">{`, ${awaitingTotal} awaiting`}</span>
                  )}
                  <NavCountBadge count={awaitingTotal} />
                </span>
              }
              id="procurement-dropdown"
              active={procurementItems.some(isCurrent)}
            >
              {procurementItems.map(renderMenuItem)}
            </NavDropdown>
          )}
          {equipmentVisible && (
            <Nav.Link
              as={NavLink}
              to="/equipment"
              onClick={() => handleNavClick('/equipment')}
            >
              <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                Equipment
                {criticalDefectCount > 0 && (
                  <span className="tw-sr-only">{`, ${criticalDefectCount} open critical ${criticalDefectCount === 1 ? 'defect' : 'defects'}`}</span>
                )}
                <NavCountBadge count={criticalDefectCount} />
              </span>
            </Nav.Link>
          )}
          {workRequestItems.length > 0 && (
            <NavDropdown
              title="Work Requests"
              id="work-requests-dropdown"
              active={workRequestItems.some(isCurrent)}
            >
              {workRequestItems.map(renderMenuItem)}
            </NavDropdown>
          )}

          {(
            hasPermission('report:view') ||
            hasPermission('report:material_usage_create') ||
            hasPermission('report:labour_submitted_time_create') ||
            hasPermission('report:finance_monthend_create') ||
            // EQ-30: the Open Defects report is for View Repairs.
            hasPermission(EQUIPMENT_PERMISSIONS.repairsView)
          ) && (
            <Nav.Link
              as={NavLink}
              to="/reports"
              onClick={() => handleNavClick('/reports')}
            >
              Reports
            </Nav.Link>
          )}

          {/* Admin: grouped flyouts. Visible if the user can open at least one page. */}
          {adminVisible && (
            <NavDropdown
              title="Admin"
              id="admin-dropdown"
              active={currentAdminGroupId !== null}
              // Reopening Admin starts with every group closed, not the one left open last time
              // (in the drawer the current page's group then expands itself - AdminGroupFlyout).
              onOpenChange={(open) => {
                if (!open) setOpenAdminGroupId(null);
              }}
            >
              {adminGroups.map(({ group, items }) => (
                <AdminGroupFlyout
                  key={group.id}
                  group={group}
                  items={items}
                  isOpen={openAdminGroupId === group.id}
                  currentPath={currentPath}
                  onOpen={() => setOpenAdminGroupId(group.id)}
                  onClose={() => setOpenAdminGroupId((current) => (current === group.id ? null : current))}
                  onNavigate={handleNavClick}
                />
              ))}
            </NavDropdown>
          )}
        </Nav>
      </Navbar.Collapse>
      {/* Outside the drawer: on the right at every width (NAV-01, NAV-11). The Message Center
          icon is the bar's utility icon, beside the account circle (V2-035). */}
      {user && messageBoardVisible && (
        <Nav.Link
          as={NavLink}
          to="/message-board"
          onClick={() => handleNavClick('/message-board')}
          aria-label="Message Center"
          title="Message Center"
          className="tw-ml-auto tw-mr-1 tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center"
        >
          <i className="bi bi-envelope tw-text-lg" aria-hidden="true" />
        </Nav.Link>
      )}
      {user && (
        <AccountMenu
          name={user.name}
          lastName={user.lastName}
          email={user.email}
          role={typeof user.role === 'string' ? user.role : user.role?.name}
          serviceAreas={(user.userServiceAreas ?? [])
            .map((usa) => usa.serviceArea?.name)
            .filter((n): n is string => Boolean(n))}
          homeForemanArea={user.defaultForemanArea?.name}
          onLogout={handleLogout}
        />
      )}
    </Navbar>
  );
};

export default NavBar;
